<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const store = useStore();
const router = useRouter();

const records = useMapGetter('agentPauseTypes/allPauseTypes');
const uiFlags = useMapGetter('agentPauseTypes/getUIFlags');
const currentAccountId = useMapGetter('getCurrentAccountId');

const showDeleteConfirmationPopup = ref(false);
const activePauseType = ref(null);

onMounted(() => {
  store.dispatch('agentPauseTypes/fetch');
});

const deleteConfirmText = computed(
  () =>
    `${t('AGENT_PAUSE_TYPES.DELETE.CONFIRM_YES')} ${activePauseType.value?.name || ''}`
);
const deleteRejectText = computed(
  () =>
    `${t('AGENT_PAUSE_TYPES.DELETE.CONFIRM_NO')} ${activePauseType.value?.name || ''}`
);
const deleteMessage = computed(
  () => ` ${activePauseType.value?.name || ''}? `
);

const openAddPage = () => {
  router.push({
    name: 'agent_pause_types_new',
    params: { accountId: currentAccountId.value },
  });
};

const openEditPage = pauseType => {
  router.push({
    name: 'agent_pause_types_edit',
    params: { accountId: currentAccountId.value, pauseTypeId: pauseType.id },
  });
};

const openDeletePopup = pauseType => {
  activePauseType.value = pauseType;
  showDeleteConfirmationPopup.value = true;
};

const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const confirmDeletion = async () => {
  const id = activePauseType.value?.id;
  closeDeletePopup();
  try {
    await store.dispatch('agentPauseTypes/destroy', id);
    useAlert(t('AGENT_PAUSE_TYPES.DELETE.SUCCESS'));
  } catch (error) {
    useAlert(error?.message || t('AGENT_PAUSE_TYPES.DELETE.ERROR'));
  }
};
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :no-records-found="!records.length"
    :no-records-message="t('AGENT_PAUSE_TYPES.NO_RECORDS')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('AGENT_PAUSE_TYPES.TITLE')"
        :description="t('AGENT_PAUSE_TYPES.DESCRIPTION')"
      >
        <template #actions>
          <Button
            :label="t('AGENT_PAUSE_TYPES.NEW_PAUSE_TYPE')"
            size="sm"
            @click="openAddPage"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div class="overflow-hidden rounded-lg border border-n-weak">
        <table class="w-full">
          <thead class="bg-n-alpha-black2">
            <tr>
              <th
                class="px-4 py-3 text-left text-xs font-medium text-n-slate-11 uppercase"
              >
                {{ t('AGENT_PAUSE_TYPES.LIST.NAME') }}
              </th>
              <th
                class="px-4 py-3 text-left text-xs font-medium text-n-slate-11 uppercase"
              >
                {{ t('AGENT_PAUSE_TYPES.LIST.DURATION') }}
              </th>
              <th
                class="px-4 py-3 text-left text-xs font-medium text-n-slate-11 uppercase"
              >
                {{ t('AGENT_PAUSE_TYPES.LIST.ACTIVE') }}
              </th>
              <th
                class="px-4 py-3 text-right text-xs font-medium text-n-slate-11 uppercase"
              >
                {{ t('AGENT_PAUSE_TYPES.LIST.ACTIONS') }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak">
            <tr v-for="pauseType in records" :key="pauseType.id">
              <td class="px-4 py-3 text-sm text-n-slate-12">
                {{ pauseType.name }}
              </td>
              <td class="px-4 py-3 text-sm text-n-slate-11">
                {{ pauseType.duration_minutes }}
                {{ t('AGENT_PAUSE_TYPES.LIST.MINUTES') }}
              </td>
              <td class="px-4 py-3 text-sm text-n-slate-11">
                <span
                  :class="
                    pauseType.active ? 'text-n-teal-9' : 'text-n-slate-9'
                  "
                >
                  {{
                    pauseType.active
                      ? t('AGENT_PAUSE_TYPES.LIST.STATUS_ACTIVE')
                      : t('AGENT_PAUSE_TYPES.LIST.STATUS_INACTIVE')
                  }}
                </span>
              </td>
              <td class="px-4 py-3 text-right">
                <div class="inline-flex items-center gap-2">
                  <Button
                    size="xs"
                    faded
                    slate
                    :label="t('AGENT_PAUSE_TYPES.EDIT_BUTTON')"
                    @click="openEditPage(pauseType)"
                  />
                  <Button
                    size="xs"
                    ruby
                    faded
                    :label="t('AGENT_PAUSE_TYPES.DELETE_BUTTON')"
                    @click="openDeletePopup(pauseType)"
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <woot-delete-modal
        v-model:show="showDeleteConfirmationPopup"
        :on-close="closeDeletePopup"
        :on-confirm="confirmDeletion"
        :title="t('AGENT_PAUSE_TYPES.DELETE.CONFIRM_TITLE')"
        :message="t('AGENT_PAUSE_TYPES.DELETE.CONFIRM_MESSAGE')"
        :message-value="deleteMessage"
        :confirm-text="deleteConfirmText"
        :reject-text="deleteRejectText"
      />
    </template>
  </SettingsLayout>
</template>
